import type {ColumnDef} from "@tanstack/react-table";
import {token} from "@atlaskit/tokens";
import type {TableConfig, BadgeConfig} from "~/components/table/table.types";
import {TableCheckbox} from "~/components/table/TableCheckbox";


export function withoutTableRowActions<TRow>(config: TableConfig<TRow>): TableConfig<TRow> {
    const {
        rowActionsConfig: _rowActionsConfig,
        rowActions: _rowActions,
        renderInlineActions: _renderInlineActions,
        onEditRow: _onEditRow,
        canEditRow: _canEditRow,
        onViewRow: _onViewRow,
        onDeleteRow: _onDeleteRow,
        canDeleteRow: _canDeleteRow,
        ...rest
    } = config;
    return {
        ...rest,
        columns: rest.columns.filter((column) => column.id !== "__actions__"),
    };
}

const DEFAULT_BADGE_COLORS = [
    {color: token("color.text.accent.green"), bgColor: token("color.background.accent.green.subtlest")},
    {color: token("color.text.accent.blue"), bgColor: token("color.background.accent.blue.subtlest")},
    {color: token("color.text.accent.yellow"), bgColor: token("color.background.accent.yellow.subtlest")},
    {color: token("color.text.accent.magenta"), bgColor: token("color.background.accent.magenta.subtlest")},
    {color: token("color.text.subtle"), bgColor: token("color.background.neutral")},
    {color: token("color.text.accent.red"), bgColor: token("color.background.accent.red.subtlest")},
    {color: token("color.text.accent.purple"), bgColor: token("color.background.accent.purple.subtlest")},
];

export function getDefaultBadgeConfig(value: string): BadgeConfig {
    const idx = value.charCodeAt(0) % DEFAULT_BADGE_COLORS.length;
    const selected = DEFAULT_BADGE_COLORS[idx]!;
    return {variant: "outline", color: selected.color, bgColor: selected.bgColor};
}

export function isSkeletonRow<TRow>(row: TRow): boolean {
    const rec = row as Record<string, unknown>;
    if (rec["__skeleton__"] === true) {
        return true;
    }
    const id = rec["id"];
    if (typeof id === "string" && id.startsWith("skeleton-")) {
        return true;
    }
    return false;
}

export function createSkeletonRows<TRow>(count: number): TRow[] {
    return Array.from({length: count}, (_, i) => ({__skeleton__: true, __skeletonIndex__: i})) as TRow[];
}

export function clsx(...classes: (string | undefined | null | false)[]): string {
    return classes.filter(Boolean).join(" ");
}

/** Shown when a cell has no value or an unresolved placeholder (e.g. backend `"?"`). */
export const TABLE_EMPTY_CELL_DISPLAY = "—" as const;

/** Values treated as empty in generic table cells (null/undefined, blank, backend `"?"`). */
export function isTableEmptyCellValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed === "" || trimmed === "?";
    }
    return false;
}

export function buildTanstackColumns<TRow>(config: TableConfig<TRow>): ColumnDef<TRow>[] {
    const cols: ColumnDef<TRow>[] = [];
    if (config.rowSelection === "multiple") {
        cols.push({
            id: "__checkbox__",
            header: ({table}) => (
                <TableCheckbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({row}) => (
                <TableCheckbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
            size: 60,
            minSize: 60,
            maxSize: 60,
            enableSorting: false,
            enableResizing: false,
            enableHiding: false,
            meta: {type: "checkbox"},
        });
    }
    for (const colDef of config.columns) {
        cols.push({
            id: colDef.id,
            header: colDef.displayName,
            accessorFn: (row) => {
                if (isSkeletonRow(row)) {return undefined;}
                try {
                    return colDef.valueGetter(row);
                } catch {
                    return undefined;
                }
            },
            size: colDef.width ?? 150,
            minSize: colDef.minWidth ?? 80,
            // Sorting is via ottopilotDataViewer toolbar, not generic table headers.
            enableSorting: false,
            // enableSorting: colDef.sortable !== false && config.sorting !== false,
            enableResizing: colDef.resizable !== false && config.columnResizing !== false,
            enableHiding: colDef.hideable !== false,
            meta: {
                colDef,
                type: colDef.type ?? "text",
                align: colDef.type === "actions" ? "center" : (colDef.align ?? "left"),
            },
        });
    }
    const hasActions =
        config.rowActionsConfig !== undefined ||
        config.onViewRow !== undefined ||
        config.onEditRow !== undefined ||
        config.onDeleteRow !== undefined ||
        config.renderInlineActions !== undefined ||
        (config.rowActions?.length ?? 0) > 0;
    const columnsAlreadyIncludeActions = config.columns.some((column) => column.id === "__actions__");
    if (hasActions && !columnsAlreadyIncludeActions) {
        /** Icon buttons use --table-action-btn-size; gap/padding aligned with .table-actions-cell / .table-td--actions. */
        const ICON = 24;
        const GAP = 8;
        const CELL_PAD = 16;
        const toolbarButtons =
            config.rowActionsConfig !== undefined
                ? config.rowActionsConfig.toolbar.length
                : Number(config.onViewRow !== undefined) +
                  Number(config.onEditRow !== undefined) +
                  Number(config.onDeleteRow !== undefined) +
                  Number((config.rowActions?.length ?? 0) > 0);
        const inlineReserve =
            config.rowActionsConfig !== undefined ? 0 : config.renderInlineActions !== undefined ? 1 : 0;
        const n = toolbarButtons + inlineReserve;
        const actionsWidth = Math.max(
            96,
            Math.min(320, CELL_PAD + n * ICON + Math.max(0, n - 1) * GAP),
        );
        cols.push({
            id: "__actions__",
            header: "Actions",
            size: actionsWidth,
            minSize: 96,
            maxSize: 320,
            enableSorting: false,
            enableResizing: false,
            enableHiding: false,
            meta: {type: "actions", align: config.actionColumnAlign ?? "center"},
        });
    }
    return cols;
}
